import React from 'react';
import { Container } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLinkedin, faGithub, faXTwitter, faFacebook,
  faYoutube, faInstagram, faWhatsapp,
} from '@fortawesome/free-brands-svg-icons';
import { faQrcode, faFileLines, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { QRCodeCanvas } from 'qrcode.react';
//import Footer from '../Footer';
import './Contact.css';

/* ─── Animation variants ─────────────────────────────────────── */
var fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.65, ease: [0.16, 1, 0.3, 1] } },
};

var stagger = {
  hidden: {},
  show:   { transition: { staggerChildren: 0.07 } },
};

/* ─── Social links ───────────────────────────────────────────── */
var SOCIALS = [
  {
    title:    'LinkedIn',
    icon:     faLinkedin,
    link:     'https://www.linkedin.com/in/jacob-chidi-eugene-035746249/',
    color:    '#0a66c2',
    handle:   'jacob-chidi-eugene',
  },
  {
    title:    'GitHub',
    icon:     faGithub,
    link:     'https://github.com/jacobeugene40/',
    color:    '#f0f0f0',
    handle:   'jacobeugene40',
  },
  {
    title:    'YouTube',
    icon:     faYoutube,
    link:     'https://www.youtube.com/@CodesolverTech',
    color:    '#ff0000',
    handle:   '@CodesolverTech',
  },
  {
    title:    'WhatsApp',
    icon:     faWhatsapp,
    link:     'https://wa.me/qr/https://whatsapp.com/dl/',
    color:    '#25d366',
    handle:   'Message me',
  },
  {
    title:    'Instagram',
    icon:     faInstagram,
    link:     'https://www.instagram.com/jacobeugene70',
    color:    '#e1306c',
    handle:   '@jacobeugene70',
  },
  {
    title:    'X / Twitter',
    icon:     faXTwitter,
    link:     'https://x.com/JacobEugene7/',
    color:    '#f0f0f0',
    handle:   '@JacobEugene7',
  },
  {
    title:    'Facebook',
    icon:     faFacebook,
    link:     'https://web.facebook.com/jacobchidie',
    color:    '#1877f2',
    handle:   'jacobchidie',
  },
  {
    title:    'Email',
    icon:     faEnvelope,
    link:     'mailto:jacobchidiugene@gmail.com',
    color:    '#c9a84c',
    handle:   'jacobchidiugene@gmail.com',
  },
];

/* ─── QR codes ───────────────────────────────────────────────── */
var QR_CODES = [
  {
    label: 'Resume',
    icon:  faFileLines,
    value: 'https://drive.google.com/file/d/19SBq75gd6nrR8Xa1fEt9kHNKWXoQ2nX2/view?usp=sharing',
    desc:  'Scan to view my full resume on Google Drive.',
  },
  {
    label: 'Cover Letter',
    icon:  faFileLines,
    value: 'https://drive.google.com/file/d/1JYcaK7zAfFu3mmuDNK5j6kYOoc5pAsyD/view?usp=sharing',
    desc:  'Scan to view my cover letter on Google Drive.',
  },
  {
    label: 'Portfolio',
    icon:  faQrcode,
    value: 'https://my-portifolio-tau-eight.vercel.app/',
    desc:  'Scan to open my live portfolio website.',
  },
];

/* ─── Component ──────────────────────────────────────────────── */
var Contact = function() {
  return (
    <motion.div
      className="contact-root"
      initial="hidden"
      animate="show"
      variants={stagger}
    >

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="contact-hero" aria-label="Contact header">
        <div className="contact-hero__glow contact-hero__glow--1" aria-hidden="true" />
        <div className="contact-hero__glow contact-hero__glow--2" aria-hidden="true" />
        <Container>
          <motion.span className="home-label" variants={fadeUp}>Get In Touch</motion.span>
          <motion.h1 className="contact-hero__title" variants={fadeUp}>
            Let's work <em>together.</em>
          </motion.h1>
          <motion.p className="contact-hero__sub" variants={fadeUp}>
            Whether you have a project in mind, a question, or just want to connect —
            reach out through any of the channels below.
          </motion.p>
        </Container>
      </section>

      {/* ── Social links ─────────────────────────────────────── */}
      <section className="contact-section" aria-labelledby="social-heading">
        <Container>
          <motion.div className="home-section__header" variants={fadeUp}>
            <span className="home-label">Connect</span>
            <h2 id="social-heading" className="home-section__title">Find Me Online</h2>
            <p className="home-section__sub">
              Follow, message, or connect — I'm active across all these platforms.
            </p>
          </motion.div>

          <motion.div className="contact-social-grid" variants={stagger}>
            {SOCIALS.map(function(s) {
              return (
                <motion.a
                  key={s.title}
                  href={s.link}
                  className="contact-social-card"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ '--social-color': s.color }}
                  variants={fadeUp}
                  whileHover={{ y: -6, transition: { duration: 0.25 } }}
                  aria-label={s.title + ' — ' + s.handle}
                >
                  <div className="contact-social-card__icon" aria-hidden="true">
                    <FontAwesomeIcon icon={s.icon} />
                  </div>
                  <div className="contact-social-card__body">
                    <span className="contact-social-card__title">{s.title}</span>
                    <span className="contact-social-card__handle">{s.handle}</span>
                  </div>
                  <div className="contact-social-card__arrow" aria-hidden="true">↗</div>
                </motion.a>
              );
            })}
          </motion.div>
        </Container>
      </section>

      {/* ── QR codes ─────────────────────────────────────────── */}
      <section className="contact-section contact-section--alt" aria-labelledby="qr-heading">
        <Container>
          <motion.div className="home-section__header" variants={fadeUp}>
            <span className="home-label">Quick Access</span>
            <h2 id="qr-heading" className="home-section__title">Scan My Documents</h2>
            <p className="home-section__sub">
              Use your phone camera to instantly access my resume, cover letter, and portfolio.
            </p>
          </motion.div>

          <motion.div className="contact-qr-grid" variants={stagger}>
            {QR_CODES.map(function(qr) {
              return (
                <motion.div
                  key={qr.label}
                  className="contact-qr-card"
                  variants={fadeUp}
                  whileHover={{ y: -4, transition: { duration: 0.25 } }}
                >
                  <div className="contact-qr-card__icon-row">
                    <FontAwesomeIcon icon={qr.icon} className="contact-qr-card__doc-icon" />
                    <span className="contact-qr-card__label">{qr.label}</span>
                  </div>
                  <div className="contact-qr-card__canvas-wrap" aria-label={'QR code for ' + qr.label}>
                    <QRCodeCanvas
                      value={qr.value}
                      size={160}
                      includeMargin={false}
                      bgColor="transparent"
                      fgColor="#eae6df"
                    />
                  </div>
                  <p className="contact-qr-card__desc">{qr.desc}</p>
                  <a
                    href={qr.value}
                    className="contact-qr-card__link"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={'Open ' + qr.label + ' link'}
                  >
                    Open Link ↗
                  </a>
                </motion.div>
              );
            })}
          </motion.div>
        </Container>
      </section>
    </motion.div>
  );
};

export default Contact;